import '@operato/data-grist'
import '@operato/input/ox-input-file.js'

import gql from 'graphql-tag'
import { css, html, LitElement } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'

import { DataGrist, FetchOption, FilterValue, PaginationConfig, SortersConfig } from '@operato/data-grist'
import { buildArgs, client } from '@operato/graphql'
import { ScrollbarStyles } from '@operato/styles'
import { i18next } from '@operato/i18n'

const FETCH_BOARD_TEMPLATE_LIST_GQL = (listParam: any) => {
  return gql`
  {
    boardTemplates(${buildArgs(listParam)}) {
      items {
        id
        name
        description
        visibility
        thumbnail
      }
      total
    }
  }
`
}

const FETCH_BOARD_TEMPLATE_GQL = (id: string) => {
  return gql`
  {
    boardTemplate(id: "${id}") {
      id
      name
      description
      visibility
      model
      thumbnail
    }
  }
`
}

@customElement('ox-board-template-list')
export class OxBoardTemplateList extends LitElement {
  static styles = [
    ScrollbarStyles,
    css`
      :host {
        display: flex;

        width: 100%;
      }

      ox-grist {
        flex: 1;
        overflow-y: auto;
      }

      #headroom {
        align-items: center;
        padding: var(--padding-default) var(--spacing-large);
        border-top: 2px solid rgba(0, 0, 0, 0.2);
        color: var(--md-sys-color-on-surface-variant);
        background-color: var(--md-sys-color-surface-variant);
        box-shadow: var(--box-shadow);
      }

      #filters {
        display: flex;
        flex-direction: row;
        place-content: space-between;
        margin: var(--margin-default) 0;
      }

      select {
        border: 0;
        outline: none;
        text-align: right;
      }

      @media only screen and (max-width: 460px) {
        #filters {
          flex-direction: column;
        }
      }
    `
  ]

  @property({ type: Boolean, attribute: 'without-search' }) withoutSearch: boolean = false

  @query('ox-grist') grist!: DataGrist

  render() {
    return html`
      <ox-grist .config=${this.gristConfig} .mode=${'CARD'} auto-fetch .fetchHandler=${this.fetchHandler.bind(this)}>
        <div slot="headroom" id="headroom">
          <div id="filters">
            <ox-filters-form autofocus .withoutSearch=${this.withoutSearch}></ox-filters-form>
          </div>
        </div>
      </ox-grist>
    `
  }

  async fetchHandler({ page = 1, limit = 100, sortings = [], filters = [] }: FetchOption) {
    const { items: records, total } = await this.getBoardTemplates({ page, limit, filters, sortings })

    return {
      total,
      records
    }
  }

  get gristConfig() {
    const PRIVATE = i18next.t('label.visibility-private')
    const DOMAIN = i18next.t('label.visibility-domain')
    const PUBLIC = i18next.t('label.visibility-public')

    const visibilities = window.location.pathname.startsWith('/domain/')
      ? [
          { display: PRIVATE, value: 'private' },
          { display: DOMAIN, value: 'domain' },
          { display: PUBLIC, value: 'public' }
        ]
      : [
          { display: PRIVATE, value: 'private' },
          { display: PUBLIC, value: 'public' }
        ]

    return {
      list: {
        thumbnail: 'thumbnail',
        fields: ['name'],
        details: ['description']
      },
      columns: [
        {
          type: 'string',
          name: 'id',
          hidden: true
        },
        {
          type: 'string',
          name: 'name',
          header: 'name',
          record: {
            editable: true,
            align: 'left'
          },
          width: 200,
          filter: 'search',
          sortable: true
        },
        {
          type: 'string',
          name: 'description',
          header: 'description',
          record: {
            editable: true,
            align: 'left'
          },
          width: 200,
          filter: 'search'
        },
        {
          type: 'select-buttons',
          name: 'visibility',
          header: 'visibility',
          record: {
            editable: false
          },
          hidden: true,
          filter: {
            operator: 'in',
            options: visibilities,
            label: ''
          }
        },
        {
          type: 'image',
          name: 'thumbnail',
          hidden: true,
          record: {
            editable: false
          },
          width: 120
        }
      ],
      rows: {
        appendable: false,
        selectable: {
          multiple: false
        },
        handlers: {
          click: 'select-row-toggle'
        }
      },
      sorters: [
        {
          name: 'name',
          desc: false
        }
      ],
      pagination: {
        pages: [20, 30, 50, 100, 200]
      }
    }
  }

  async firstUpdated() {
    this.refreshBoardTemplates()
  }

  async getSelected() {
    const selected = this.grist.selected
    const templateId = selected && selected[0]?.id

    if (!templateId) {
      return
    }

    var boardTemplateResponse = await client.query({
      query: FETCH_BOARD_TEMPLATE_GQL(templateId)
    })

    var template = boardTemplateResponse.data?.boardTemplate

    if (template) {
      template = {
        ...template,
        model: JSON.parse(template.model)
      }
    }

    return template
  }

  async refreshBoardTemplates() {
    this.grist.fetch()
  }

  async getBoardTemplates({
    page = 1,
    limit = 30,
    filters = [],
    sortings = []
  }: { page?: number; limit?: number; filters?: FilterValue[]; sortings?: SortersConfig } = {}) {
    var pagination: PaginationConfig = {
      limit,
      page
    }

    var params = {
      filters,
      sortings,
      pagination
    }

    var boardTemplateListResponse = await client.query({
      query: FETCH_BOARD_TEMPLATE_LIST_GQL(params)
    })

    return boardTemplateListResponse?.data?.boardTemplates || {}
  }
}
